import http from "./http-common";
import { getAuthToken } from "./authService";

const TERMINAL_BACKEND_STATUSES = new Set([
  "PUBLISHED",
  "REJECTED",
  "FAILED",
  "CANCELLED",
]);

const STALL_TIMEOUT_MS = 90_000;
const POLL_INTERVAL_MS = 60_000;
const RECONNECT_BASE_MS = 2_000;
const RECONNECT_MAX_MS = 30_000;
// Backstop /status poll back-off: on Hikari saturation the server can 5xx for minutes.
// Without back-off, every SSE-stall watcher would keep hitting /status every 60 s and
// deepen the outage. Cap at 5 minutes so a recovered server is picked up within one cap
// window. Network errors keep the base cadence — user Wi-Fi flapping shouldn't slow the
// watcher's recovery poll.
const POLL_BACKOFF_CAP = 5;
const POLL_MAX_MS = 5 * 60_000;

const isServerErrorResponse = (err) => {
  const status = err?.response?.status;
  return typeof status === "number" && status >= 500 && status < 600;
};

const computeBackstopDelay = (consecutive5xx) => {
  if (!consecutive5xx) return POLL_INTERVAL_MS;
  const multiplier = Math.pow(2, Math.min(consecutive5xx, POLL_BACKOFF_CAP));
  return Math.min(POLL_MAX_MS, POLL_INTERVAL_MS * multiplier);
};

/**
 * Open an SSE stream for a package upload. Returns an object with .close() so the caller can
 * tear it down on unmount/cancel.
 *
 * Native EventSource cannot send Authorization headers, so this uses fetch + ReadableStream and a
 * minimal SSE parser. Heartbeats from the server arrive as `event: ping`; if nothing arrives for
 * STALL_TIMEOUT_MS we fall back to /api/v1/metadata/status polling, and we keep trying to
 * reconnect SSE in the background.
 */
export const openUploadEventsStream = ({ uploadId, onEvent, onError } = {}) => {
  if (!uploadId) {
    throw new Error("openUploadEventsStream requires uploadId");
  }
  if (typeof onEvent !== "function") {
    throw new Error("openUploadEventsStream requires onEvent callback");
  }

  let closed = false;
  let abortController = null;
  let stallTimer = null;
  let reconnectTimer = null;
  let pollTimer = null;
  let reconnectAttempts = 0;
  let pollInFlight = false;

  const baseUrl = http.defaults.baseURL || "";
  const url = `${baseUrl}/api/v1/upload/${uploadId}/events`;

  const clearStallTimer = () => {
    if (stallTimer) {
      clearTimeout(stallTimer);
      stallTimer = null;
    }
  };

  const armStallTimer = () => {
    clearStallTimer();
    stallTimer = setTimeout(() => {
      if (closed) return;
      startPollingBackstop();
    }, STALL_TIMEOUT_MS);
  };

  const close = () => {
    closed = true;
    clearStallTimer();
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (pollTimer) {
      clearTimeout(pollTimer);
      pollTimer = null;
    }
    if (abortController) {
      try { abortController.abort(); } catch {}
      abortController = null;
    }
  };

  const handleStatusPayload = (payload) => {
    const normalizedPayload = payload?.status || !payload?.originalStatus
      ? payload
      : { ...payload, status: payload.originalStatus };
    onEvent(normalizedPayload);
    const backendStatus = normalizedPayload?.status;
    if (backendStatus && TERMINAL_BACKEND_STATUSES.has(backendStatus)) {
      close();
    }
  };

  let backstopConsecutive5xx = 0;
  const scheduleBackstopTick = (delayMs) => {
    if (closed) return;
    pollTimer = setTimeout(async () => {
      pollTimer = null;
      if (closed) return;
      if (pollInFlight) {
        scheduleBackstopTick(POLL_INTERVAL_MS);
        return;
      }
      pollInFlight = true;
      let nextDelay = POLL_INTERVAL_MS;
      try {
        const token = await getAuthToken();
        const response = await http.get("/api/v1/metadata/status", {
          headers: { Authorization: `Bearer ${token}` },
          params: { uploadId },
        });
        backstopConsecutive5xx = 0;
        if (response?.data) {
          handleStatusPayload(response.data);
        }
      } catch (err) {
        if (isServerErrorResponse(err)) {
          backstopConsecutive5xx += 1;
          nextDelay = computeBackstopDelay(backstopConsecutive5xx);
        }
        if (typeof onError === "function") {
          onError(err);
        }
      } finally {
        pollInFlight = false;
      }
      scheduleBackstopTick(nextDelay);
    }, delayMs);
  };

  const startPollingBackstop = () => {
    if (closed || pollTimer) return;
    backstopConsecutive5xx = 0;
    scheduleBackstopTick(POLL_INTERVAL_MS);
  };

  const scheduleReconnect = () => {
    if (closed) return;
    const delay = Math.min(
      RECONNECT_MAX_MS,
      RECONNECT_BASE_MS * Math.pow(2, Math.min(reconnectAttempts, 5))
    );
    reconnectAttempts += 1;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      connect();
    }, delay);
  };

  const parseSseBuffer = (buffer) => {
    // Split on SSE event boundary (blank line). Keep trailing partial in the buffer.
    const events = [];
    let remaining = buffer;
    let idx;
    while ((idx = remaining.indexOf("\n\n")) !== -1) {
      const raw = remaining.slice(0, idx);
      remaining = remaining.slice(idx + 2);
      events.push(raw);
    }
    return { events, remaining };
  };

  const interpretEvent = (raw) => {
    let eventName = "message";
    const dataLines = [];
    for (const line of raw.split("\n")) {
      if (!line || line.startsWith(":")) continue;
      const colon = line.indexOf(":");
      const field = colon === -1 ? line : line.slice(0, colon);
      const value = colon === -1 ? "" : line.slice(colon + 1).trimStart();
      if (field === "event") eventName = value;
      else if (field === "data") dataLines.push(value);
    }
    return { eventName, data: dataLines.join("\n") };
  };

  const connect = async () => {
    if (closed) return;
    armStallTimer();
    abortController = new AbortController();

    let token;
    try {
      token = await getAuthToken();
    } catch (err) {
      if (typeof onError === "function") onError(err);
      scheduleReconnect();
      return;
    }

    let response;
    try {
      response = await fetch(url, {
        method: "GET",
        signal: abortController.signal,
        headers: {
          Accept: "text/event-stream",
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
        cache: "no-store",
      });
    } catch (err) {
      if (closed) return;
      if (typeof onError === "function") onError(err);
      scheduleReconnect();
      return;
    }

    if (!response.ok || !response.body) {
      if (typeof onError === "function") {
        onError(new Error(`SSE response status=${response.status}`));
      }
      if (response.status === 401 || response.status === 403) {
        startPollingBackstop();
        scheduleReconnect();
        return;
      }
      scheduleReconnect();
      return;
    }

    reconnectAttempts = 0;
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (!closed) {
        const { value, done } = await reader.read();
        if (done) break;
        armStallTimer();
        buffer += decoder.decode(value, { stream: true });
        const parsed = parseSseBuffer(buffer);
        buffer = parsed.remaining;
        for (const raw of parsed.events) {
          const { eventName, data } = interpretEvent(raw);
          if (!data) continue;
          if (eventName === "ping") continue;
          let payload;
          try {
            payload = JSON.parse(data);
          } catch {
            payload = data;
          }
          if (eventName === "status") {
            handleStatusPayload(payload);
          } else {
            onEvent({ event: eventName, data: payload });
          }
        }
      }
    } catch (err) {
      if (!closed && typeof onError === "function") onError(err);
    } finally {
      try { reader.releaseLock(); } catch {}
    }

    if (!closed) {
      scheduleReconnect();
    }
  };

  connect();

  return { close };
};
